import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

/* ============================================================
   SpectraIQ theme system
   ------------------------------------------------------------
   Two orthogonal selectors:
     - theme   : visual identity (palette, fonts, radii, vibe)
     - mode    : 'dark' | 'light'

   The <html> element receives classes like:
     theme-spectra mode-dark
     theme-mint    mode-light
   ...and the matching CSS variable block in index.css takes over.

   Defaults: theme = 'office365', mode = 'light'.
   ============================================================ */

const STORAGE_THEME = 'spectraiq.theme';
const STORAGE_MODE  = 'spectraiq.mode';

export const THEMES = {
  spectra: {
    id: 'spectra',
    label: 'SpectraIQ',
    description: 'Default — premium glass with violet accent',
    swatchDark:  ['#8b5cf6', '#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'],
    swatchLight: ['#7c3aed', '#4f46e5', '#2563eb', '#059669', '#d97706', '#dc2626'],
  },
  office365: {
    id: 'office365',
    label: 'Office 365',
    description: 'Microsoft Fluent — signature blue, crisp professional tiles',
    swatchDark:  ['#2899f5', '#4a8fd6', '#3aa856', '#e8633f', '#a86fd6', '#e9636e'],
    swatchLight: ['#0078d4', '#2b579a', '#217346', '#d24726', '#7719aa', '#d13438'],
  },
  mint: {
    id: 'mint',
    label: 'Mint',
    description: 'Power BI style — yellow accent, crisp tiles',
    swatchDark:  ['#f2c811', '#118dff', '#12239e', '#e66c37', '#6b007b', '#e044a7'],
    swatchLight: ['#f2c811', '#118dff', '#12239e', '#e66c37', '#6b007b', '#e044a7'],
  },
  standard: {
    id: 'standard',
    label: 'Standard',
    description: 'Zoho style — warm reds and rounded inputs',
    swatchDark:  ['#e42527', '#ff9f1c', '#f1a208', '#226ce0', '#0ca678', '#6f42c1'],
    swatchLight: ['#e42527', '#ff9f1c', '#f1a208', '#226ce0', '#0ca678', '#6f42c1'],
  },
  cool: {
    id: 'cool',
    label: 'Cool',
    description: 'Tableau style — muted blues, analytical',
    swatchDark:  ['#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f', '#edc948'],
    swatchLight: ['#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f', '#edc948'],
  },
};

export const THEME_IDS = Object.keys(THEMES);
export const MODES = ['dark', 'light'];

const ThemeCtx = createContext({
  theme: 'office365',
  mode: 'light',
  setTheme: () => {},
  setMode: () => {},
  toggleMode: () => {},
  themes: THEMES,
});

function safeRead(key, fallback, allowed) {
  if (typeof window === 'undefined') return fallback;
  try {
    const v = localStorage.getItem(key);
    if (v && allowed.includes(v)) return v;
  } catch (_) { /* ignore */ }
  return fallback;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => safeRead(STORAGE_THEME, 'office365', THEME_IDS));
  const [mode,  setModeState]  = useState(() => safeRead(STORAGE_MODE,  'light',     MODES));

  useEffect(() => {
    const root = document.documentElement;
    // Strip any previous theme- / mode- classes.
    [...root.classList].forEach(c => {
      if (c.startsWith('theme-') || c.startsWith('mode-')) root.classList.remove(c);
    });
    root.classList.add(`theme-${theme}`);
    root.classList.add(`mode-${mode}`);
    // Mirror to data attrs for any consumer that prefers attribute selectors.
    root.dataset.theme = theme;
    root.dataset.mode  = mode;
    try {
      localStorage.setItem(STORAGE_THEME, theme);
      localStorage.setItem(STORAGE_MODE, mode);
    } catch (_) { /* ignore */ }
  }, [theme, mode]);

  const setTheme = useCallback((t) => { if (THEMES[t]) setThemeState(t); }, []);
  const setMode  = useCallback((m) => { if (MODES.includes(m)) setModeState(m); }, []);
  const toggleMode = useCallback(() => setModeState((m) => (m === 'dark' ? 'light' : 'dark')), []);

  const value = useMemo(() => ({
    theme, mode, setTheme, setMode, toggleMode, themes: THEMES,
  }), [theme, mode, setTheme, setMode, toggleMode]);

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() { return useContext(ThemeCtx); }
